// AGENT tab — the real pane (F-AGENT PR-B). A header strip (the active-case
// anchor), the message list (or an empty state), and the composer. Pure
// BE-consumption via `useAgent` (the SSE port) + `useCaseStore` (the active
// anchor). Rebuilds the throwaway mockup `panes/PaneAgent` in Tailwind.
//
// Invariants:
//   W1 — no business logic. The pane sends turns + renders the streamed
//        answer; the BE owns every reasoning step.
//   W2 — no Office.js. The only insert affordance is the disabled
//        `InsertAffordance` (deferred to F-AGENT-INSERT).
//   W6 — NO model/token/$ chip in the header (the mockup `haiku-4.5` kbd is
//        explicitly dropped — THE-362 firewall).
//   W7 — citation chips come from the BE registry payload only.
//   §9 — logical RTL utilities; BE answer text gets `bidiProps` per-bubble.
//
// Gate: with no active case the pane shows a ComingSoon-style hint to pick a
// dossier (mirrors `plan/BriefRoster`'s empty state) and the composer is
// disabled.

import { useTranslation } from 'react-i18next'
import { useAgent } from '@/ui/hooks/use-agent'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { MessageList } from './MessageList'
import { Composer } from './Composer'

export function AgentPane(): JSX.Element {
  const { t } = useTranslation()
  const { messages, statusText, send } = useAgent()

  // Narrow selector — only the active case id drives the gate + the anchor.
  const activeCaseId = useCaseStore((s) => s.activeContext?.caseId ?? null)
  const hasCase = activeCaseId != null

  return (
    <div data-testid="agent-pane" className="flex flex-1 flex-col">
      {/* Header strip — the case anchor (NO model/token/$ chip, W6). */}
      <div className="flex items-center gap-2 border-b border-ink/10 bg-white px-3.5 py-2.5">
        <span
          aria-hidden="true"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-xs font-bold text-gold"
        >
          ✦
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[11.5px] font-semibold text-ink">
            {t('agent.header.title')}
          </span>
          {hasCase && (
            <span
              data-testid="agent-header-anchor"
              className="truncate text-[10px] text-ink/60"
            >
              {t('agent.header.anchoredCase', {
                caseId: activeCaseId.slice(0, 8),
              })}
            </span>
          )}
        </div>
      </div>

      {/* Body — gate / empty / message list. */}
      {!hasCase ? (
        <div
          data-testid="agent-no-case"
          className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-8 text-center"
        >
          <div className="font-serif text-sm font-semibold text-ink">
            {t('agent.noCase.title')}
          </div>
          <p className="max-w-[240px] text-xs leading-relaxed text-ink/60">
            {t('agent.noCase.body')}
          </p>
        </div>
      ) : messages.length === 0 ? (
        <div
          data-testid="agent-empty"
          className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-8 text-center"
        >
          <div className="font-serif text-sm font-semibold text-ink">
            {t('agent.empty.title')}
          </div>
          <p className="max-w-[240px] text-xs leading-relaxed text-ink/60">
            {t('agent.empty.body')}
          </p>
        </div>
      ) : (
        <MessageList messages={messages} />
      )}

      {/* Live status pill — only while a node label is set (streaming). */}
      {hasCase && statusText.length > 0 && (
        <div
          data-testid="agent-status"
          role="status"
          className="border-t border-ink/10 bg-gold/[0.06] px-3.5 py-1.5 text-[10px] font-medium text-ink/70"
        >
          {statusText}
        </div>
      )}

      <Composer enabled={hasCase} onSend={send} />
    </div>
  )
}
